/** @format */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, TextField, Box } from '@material-ui/core';
import { CheckCircleOutline } from '@material-ui/icons';
// import {ArrowRight} from '@material-ui/icons';
import useAuth from 'src/hooks/useAuth';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Breadcrumb from 'src/components/Breadcrumb';
import { VALIDATE_REGEX } from 'src/constants';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { changePassword } from 'src/slices/users';
import { Button } from 'src/components';

const useStyles = makeStyles(theme => ({
  root: {
    fontSize: '1.5rem',
    margin: '0 auto'
  },
  card: {
    padding: '2.4rem 1.6rem',
    margin: '2.4rem 0 0',
    background: '#f1f2f4',
    fontSize: '16px',
    color: '#333',
    position: 'relative',
    borderRadius: '1.6rem',
    boxShadow: 'none',
    [theme.breakpoints.up('md')]: {
      padding: '4rem ',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start'
    }
  },
  textColor: {
    color: '#999',
    lineHeight: '2.5rem',
    paddingTop: '1rem'
  },
  btnwrapper: {
    marginTop: 15
  },
  instructionWrapper: {
    margin: '0 8px',
    marginTop: '15px',
    marginBottom: '15px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.8rem'
  },
  instruction: {
    color: '#666',
    verticalAlign: 'middle'
  },
  instructionValid: {
    color: 'rgb(37,189,4)'
  },
  icon: {
    color: '#999',
    verticalAlign: 'middle',
    fontSize: '2.5rem'
  },
  iconValid: {
    color: 'rgb(37,189,4)'
  },
  btnStyle: {
    border: '2px solid #333',
    borderRadius: 25,
    fontSize: '1.2rem',
    color: '#333',
    fontWeight: '700',
    '&:hover': {
      background: 'transparent',
      border: '2px solid #333'
    }
  },
  btnStyleDisabled: {
    border: '2px solid #333',
    borderRadius: 25,
    fontSize: '1.2rem',
    color: '#333',
    fontWeight: '700',
    '&:hover': {
      background: 'transparent',
      border: '2px solid #333'
    },
    opacity: '0.5',
    pointerEvents: 'none',
    cursor: 'not-allowed'
  },
  delbtnStyle: {
    marginLeft: '1.5rem',
    borderRadius: 25,
    fontSize: '1.2rem',
    color: '#999',
    fontWeight: '700',
    '&:hover': {
      background: 'transparent'
    }
  },
  textField: {
    '& > *': {
      fontSize: '1.5rem',
      color: '#333'
    },
    '& > .MuiInput-underline': {
      '&:before': {
        borderBottom: '2px solid #234D96'
      }
    },
    '& > .MuiInputLabel-formControl': {
      color: '#234D96'
    }
  },
  cardHead: {
    width: 'auto',
    maxWidth: '693px',
    flex: 'auto'
  },
  footerBtn: {
    marginTop: 25,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16
  },
  abortBtn: {
    marginRight: '1.5rem'
  },
  errorMsg: {
    color: '#a00'
  },
  successMsg: {
    color: ''
  }
}));

const Access = props => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const classes = useStyles();
  let history = useHistory();
  const [userData, setUserData] = useState(null);
  const goBack = () => {
    history.push('/profile');
  };
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState(undefined);
  const [successMsg, setSuccessMsg] = useState(undefined);
  const [specialCase, setSpecialCase] = useState(false);
  const [upperCase, setUpperCase] = useState(false);
  const [number, setNumber] = useState(false);
  const [length, setLength] = useState(false);

  const isValidPassword = [specialCase, upperCase, number, length].every(i => i) && password === confirmPassword;

  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  const savePassword = () => {
    let payload = {
      uuid: user.uuid,
      password: password
    };
    if (isValidPassword) {
      dispatch(changePassword(payload)).then(response => {
        if (response) {
          setSuccessMsg(t('Access.successMsg'));
          setTimeout(reset, 3000);
        }
      });
    }
  };

  const reset = () => {
    setConfirmPassword('');
    setPassword('');
    setSuccessMsg(undefined);
    setSpecialCase(false);
    setUpperCase(false);
    setNumber(false);
    setLength(false);
  };

  const passwordValidate = e => {
    const { value } = e.target;
    setPassword(value);
    validateHighlight(value);
    if (confirmPassword && confirmPassword === value) {
      let validation = regexValidate(value);
      if (validation) {
        setErrorMsg(undefined);
      } else {
        setErrorMsg(t('Access.reqPasswordNotFulfil'));
      }
    } else if (confirmPassword) {
      setErrorMsg(t('Access.passwordNotMatch'));
    } else if (!confirmPassword) {
      setErrorMsg(undefined);
    }
  };

  const confirmPasswordValidate = e => {
    const { value } = e.target;
    setConfirmPassword(value);
    if (password === value) {
      let validation = regexValidate(value);
      if (validation) {
        setErrorMsg(undefined);
      } else {
        setErrorMsg(t('Access.reqPasswordNotFulfil'));
      }
    } else {
      setErrorMsg(t('Access.passwordNotMatch'));
    }
  };

  const regexValidate = value => {
    return VALIDATE_REGEX.password.test(value);
  };

  const validateHighlight = value => {
    let specialCaseLetters = /[!,;`~'":{}<>^/@#$%&*?|\\[\])(+=._-]/;

    if (value.match(specialCaseLetters)) {
      setSpecialCase(true);
    } else {
      setSpecialCase(false);
    }

    // Validate capital letters
    let upperCaseLetters = /[A-Z]/g;
    if (value.match(upperCaseLetters)) {
      setUpperCase(true);
    } else {
      setUpperCase(false);
    }

    // Validate numbers
    let numbers = /[0-9]/g;
    if (value.match(numbers)) {
      setNumber(true);
    } else {
      setNumber(false);
    }

    // Validate length
    if (value.length >= 8) {
      setLength(true);
    } else {
      setLength(false);
    }
  };

  const breadcrumbData = [
    {
      title: t('profile.title'),
      link: '/profile',
      options: [
        {
          title: t('profile.title'),
          link: '/profile'
        },
        {
          title: t('personalData.title'),
          link: '/personaldata'
        },
        {
          title: t('Access.title'),
          link: '/access'
        }
      ]
    },
    {
      title: t('Access.title')
    }
  ];

  return (
    <section className="main_content profile">
      <Breadcrumb data={breadcrumbData} />
      <div className=" container">
        {/* <h4 className="page_subtitle">{t('profile.subTitle')}</h4> */}
        <Grid className="personal-data-container">
          <h2 className="page_title">{t('Access.title')}</h2>
          <Grid className={classes.card}>
            <h4 className="sub_head">{t('Access.username')}</h4>
            <Grid className={classes.cardHead}>
              <Grid>
                <TextField
                  id="standard-full-width-username"
                  label={t('Access.username')}
                  placeholder={userData?.email}
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  InputProps={{
                    readOnly: true
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid className={classes.card}>
            <h4 className="sub_head">{t('Access.changepassword')}</h4>
            <Grid className={classes.cardHead}>
              <Grid>
                <TextField
                  type="password"
                  id="standard-full-width-password"
                  label={t('Access.NewPassword')}
                  placeholder={t('Access.NewPassword')}
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={password}
                  onChange={e => passwordValidate(e)}
                />
              </Grid>
              <Grid className={classes.instructionWrapper}>
                <div className={clsx(classes.instruction, length && classes.instructionValid)}>
                  <CheckCircleOutline className={clsx(classes.icon, length && classes.iconValid)} />{' '}
                  {t('Access.instruction_1')}
                </div>
                <div className={clsx(classes.instruction, upperCase && classes.instructionValid)}>
                  <CheckCircleOutline className={clsx(classes.icon, upperCase && classes.iconValid)} />{' '}
                  {t('Access.instruction_2')}
                </div>
                <div className={clsx(classes.instruction, number && classes.instructionValid)}>
                  <CheckCircleOutline className={clsx(classes.icon, number && classes.iconValid)} />{' '}
                  {t('Access.instruction_3')}
                </div>
                <div className={clsx(classes.instruction, specialCase && classes.instructionValid)}>
                  <CheckCircleOutline className={clsx(classes.icon, specialCase && classes.iconValid)} />{' '}
                  {t('Access.instruction_4')}
                </div>
              </Grid>
              <Grid>
                <TextField
                  type="password"
                  id="standard-full-width-re-password"
                  label={t('Access.repeatNewPassword')}
                  placeholder={t('Access.repeatNewPassword')}
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={confirmPassword}
                  onChange={e => confirmPasswordValidate(e)}
                />
              </Grid>

              {errorMsg && (
                <Box sx={{ mt: 2 }}>
                  <span className={classes.errorMsg}>{errorMsg}</span>
                </Box>
              )}
              {successMsg && (
                <Box sx={{ mt: 2 }}>
                  <span className={classes.successMsg}>{successMsg}</span>
                </Box>
              )}
            </Grid>
          </Grid>
          <Grid>
            <div className={classes.footerBtn}>
              <Button onClick={goBack} ghost noArrow label={t('personalData.Abortbtn')} />
              <Button disabled={!isValidPassword} onClick={savePassword} label={t('personalData.saveBtn')} />
            </div>
          </Grid>
        </Grid>
      </div>
    </section>
  );
};

export { Access };
